/**
 * Anthropic SDK adapter for edit_file tool
 */

import type { Tool } from '@anthropic-ai/sdk/resources/messages';
import { executeEditFile } from './core.js';
import { EDIT_FILE_TOOL_DESCRIPTION, EDIT_FILE_SYSTEM_PROMPT } from './prompts.js';
import type { EditFileInput, EditFileResult, EditFileConfig } from './types.js';

/**
 * Anthropic-native tool definition for edit_file
 * 
 * @example
 * ```ts
 * import Anthropic from '@anthropic-ai/sdk';
 * import { editFileTool } from 'morphsdk/tools/anthropic';
 * 
 * const client = new Anthropic({ apiKey: process.env.ANTHROPIC_API_KEY });
 * 
 * const response = await client.messages.create({
 *   model: "claude-sonnet-4-5-20250929",
 *   tools: [editFileTool],
 *   messages: [{ role: "user", content: "Fix the bug in app.ts" }]
 * });
 * ```
 */
export const editFileTool: Tool = {
  name: 'edit_file',
  description: EDIT_FILE_TOOL_DESCRIPTION,
  input_schema: {
    type: 'object',
    properties: {
      target_filepath: {
        type: 'string',
        description: 'The path of the target file to modify',
      },
      instruction: {
        type: 'string',
        description: 'A single sentence written in the first person describing what the agent is changing. Used to help disambiguate uncertainty in the edit.',
      },
      code_edit: {
        type: 'string',
        description: 'Specify ONLY the precise lines of code that you wish to edit. Use `// ... existing code ...` for unchanged sections.',
      },
    },
    required: ['target_filepath', 'instruction', 'code_edit'],
  },
};

/**
 * Format the result for passing back to Claude
 * 
 * @param result - The edit result
 * @returns Formatted string for tool_result
 */
export function formatResult(result: EditFileResult): string {
  if (!result.success) {
    return `Error editing file: ${result.error}`;
  }
  
  const { changes } = result;
  return `Successfully applied changes to ${result.filepath}. Added ${changes.linesAdded}, removed ${changes.linesRemoved}, modified ${changes.linesModified}.`;
}

/**
 * Create a custom edit_file tool with configuration
 * 
 * @param config - Configuration options
 * @returns Tool definition with execute and formatResult methods
 * 
 * @example
 * ```ts
 * const tool = createEditFileTool({
 *   baseDir: './src',
 *   generateUdiff: true,
 *   morphApiKey: 'sk-...',
 *   description: 'Custom tool description for your use case'
 * });
 * 
 * // Use as Anthropic tool
 * const response = await client.messages.create({
 *   tools: [tool], // tool itself is the Tool definition
 *   ...
 * });
 * 
 * // Execute and format
 * const result = await tool.execute(toolUseBlock.input);
 * const formatted = tool.formatResult(result);
 * ```
 */
export function createEditFileTool(config: EditFileConfig = {}) {
  const toolDef: Tool = {
    ...editFileTool,
    ...(config.description && { description: config.description }),
  };
  
  return Object.assign({}, toolDef, {
    execute: async (input: EditFileInput): Promise<EditFileResult> => {
      return executeEditFile(input, config);
    },
    formatResult: (result: EditFileResult): string => {
      return formatResult(result);
    },
    getSystemPrompt: (): string => {
      return EDIT_FILE_SYSTEM_PROMPT;
    },
  });
}

/**
 * Execute the edit_file tool with raw input
 * 
 * @param input - Tool input from Claude's tool_use
 * @param config - Configuration for Fast Apply
 * @returns Tool result to send back to Claude
 */
export async function execute(
  input: EditFileInput,
  config?: EditFileConfig
): Promise<EditFileResult> {
  return executeEditFile(input, config);
}

/**
 * Get the system prompt for the edit_file tool
 * 
 * @returns System prompt string
 */
export function getSystemPrompt(): string {
  return EDIT_FILE_SYSTEM_PROMPT;
}
